import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type OrgAccessState = "active" | "trial" | "expired" | "pending_deletion";

export interface OrgAccess {
  /**
   * 'active' = paid/trialing Stripe subscription, 'trial' = card-free signup trial,
   * 'pending_deletion' = soft-deleted, in the 30-day grace window (read-only).
   */
  state: OrgAccessState;
  trialEndsAt: string | null;
  /** Whole days left in the signup trial, floored at 0. Null when there is no trial date. */
  daysLeft: number | null;
  /** Set when the org is pending deletion (organizations.deleted_at). */
  deletedAt: string | null;
  /** When the org will be purged: deleted_at + 30 days. Null unless pending deletion. */
  deletionScheduledAt: string | null;
  /** Mirrors org_can_write() in the DB. Enforcement itself lives in DB triggers. */
  canWrite: boolean;
}

/**
 * Entitlement state for the banner and for disabling edit affordances.
 *
 * Backed by org_access_summary(), which resolves Stripe first: an org with a
 * live subscription is 'active' even if its signup trial_ends_at is long past.
 * This is UX only -- the authoritative block on writes is in the database.
 */
export function useOrgAccess(organizationId: string | undefined) {
  return useQuery<OrgAccess>({
    queryKey: ["org-access", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("org_access_summary", {
        p_org_id: organizationId!,
      });

      if (error) throw error;

      // Set-returning function: PostgREST hands back an array of rows.
      const row = (Array.isArray(data) ? data[0] : data) as
        | {
            state?: string;
            trial_ends_at?: string | null;
            days_left?: number | null;
            deleted_at?: string | null;
            deletion_scheduled_at?: string | null;
          }
        | undefined;

      // Fail closed on a missing row: treat as expired rather than granting access.
      const state = (row?.state ?? "expired") as OrgAccessState;

      return {
        state,
        trialEndsAt: row?.trial_ends_at ?? null,
        daysLeft: row?.days_left ?? null,
        deletedAt: row?.deleted_at ?? null,
        deletionScheduledAt: row?.deletion_scheduled_at ?? null,
        canWrite: state !== "expired" && state !== "pending_deletion",
      };
    },
    enabled: !!organizationId,
  });
}
